import { DateTime } from 'luxon';

export function getGMTDate(date: DateTime): DateTime {
    return date.setZone('Europe/London');
}

/**
 * Gets the current time in GMT
 */
export function getCurrentGMTDate(): DateTime {
    return DateTime.now().setZone('Europe/London');
}

export function isDST(): boolean {
    return DateTime.now().setZone('Europe/London').isInDST;
}

export function isPastClockIn(shift: { start_date_time: DateTime, clocked_in: boolean }): boolean {
    const now = DateTime.utc();
    const shiftStartUTC = shift.start_date_time;

    const bufferMinutes = isDST() ? 90 : 30;
    const earliestClockIn = shiftStartUTC.minus({ minutes: bufferMinutes });

    return now >= earliestClockIn;
}

/**
 * Checks if a shift can be clocked into based on GMT time
 * Allows clock-in 30 minutes before shift start time
 * During DST, adds an extra hour buffer to account for time changes
 */
export function canClockInGMT(shift: { start_date_time: DateTime, clocked_in: boolean }): boolean {
    const now = DateTime.utc();
    const shiftStartUTC = shift.start_date_time;
    
    // Apply buffer: 30 minutes normally, 90 minutes during DST
    const bufferMinutes = isDST() ? 90 : 30;
    const earliestClockIn = shiftStartUTC.minus({ minutes: bufferMinutes });

    return !shift.clocked_in && now >= earliestClockIn;
}

/**
 * Checks if a shift can be completed based on GMT time
 * Only allows completion after shift end time
 */
export function canCompleteGMT(shift: { end_date_time: DateTime, clocked_in: boolean }): boolean {
    const now = DateTime.utc();
    const shiftEndUTC = shift.end_date_time;

    const bufferMinutes = isDST() ? 60 : 0;
    const updatedNow = now.plus({ minutes: bufferMinutes });

    return shift.clocked_in && updatedNow >= shiftEndUTC;
}

/**
 * Returns a string indicating the shift start time
 * If the shift has already started, returns "Started <time ago>"
 * Otherwise, returns "Starts <time until>"
 */
export const getShiftStartsInString = (shift: { start_date_time: DateTime, clocked_in: boolean }) => {
    const now = getCurrentGMTDate();
    const shiftStart = getGMTDate(shift.start_date_time);
    const adjustedShiftStart = isDST() ? shiftStart.minus({ minutes: 60 }) : shiftStart;

    if (now > adjustedShiftStart) {
        return "Started " + adjustedShiftStart.toRelative({ base: now });
    }

    return "Starts " + adjustedShiftStart.toRelative({ base: now });
}
